import { BlockError } from '@/blocks.04-project/block-error/block-error';
import productCardBase from '@/blocks.04-project/product-card/product-card.tmpl-specs/base.module';
import {API} from '@/api/config';
import toArray from 'lodash-es/toArray';
import forEach from 'lodash-es/forEach';
import debounce from 'lodash-es/debounce';
import trim from 'lodash-es/trim';
import values from 'lodash-es/values';
import get from 'lodash-es/get';
import map from 'lodash-es/map';
import isEmpty from 'lodash-es/isEmpty';
import escape from 'lodash-es/escape';
import filter from 'lodash-es/filter';

/**
 * Класс поиска
 *
 * @example
 * Дата аттрибуты:
 * data-iv-toggle="search" - открывает поиск
 *
 * Дата аттрибуты на элементах:
 * data-history - контейнер для истории поиска
 * data-search-by - вставка данных в поиск
 * data-delete-history-item - удаление поискового запроса из истории
 * data-clear-history - очистить историю поиска
 * data-submit - переадресация на страницу поиска
 * data-close-search - закрытие или очистка поиска
 *
 * Классы:
 * .search.open - поиск открыт
 * body.search-open - поиск открыт для скрытия скролла
 * .search.search_loader - ожидание поискового запроса
 * .search.search_not-found - поиск не дал результатов
 *
 * Переменные:
 * search-history - ключ для получения истории поиска
 * --header-body-height - высота тела шапки
 */
class Search {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент формы с классом search
   */
  constructor(elem) {
    this.BlockError = new BlockError({ blockName: 'Search', destroy: this.destroy });
    if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.Search = this;

		window.getBH((bh) => this.bh = bh);
    this.init();
  }

  init = () => {
		this.triggers = toArray(document.querySelectorAll('[data-iv-toggle="search"]'));

		this.input = this.block.querySelector('.form-control');
		if (!this.input) return this.BlockError.error('Отсутствует .form-control');

		this.content = this.block.querySelector('.search__menu-inner');
		if (!this.content) return this.BlockError.error('Отсутствует .search__menu-inner');

		this.historyContainer = this.block.querySelector('[data-history]');
		if (!this.historyContainer) return this.BlockError.error('Отсутствует [data-history]');

		this.notFoundContainer = this.block.querySelector('.search__not-found');
		if (!this.notFoundContainer) return this.BlockError.error('Отсутствует .search__not-found');

		this.header = document.querySelector('.header');
		if (!this.header) return this.BlockError.error('Отсутствует .header');

		this.headerBody = this.header.querySelector('.header__body');
		if (!this.headerBody) return this.BlockError.error('Отсутствует .header__body');

		this.headerOffcanvas = this.header.querySelector('.header__offcanvas');
		if (!this.headerOffcanvas) return this.BlockError.error('Отсутствует .header__offcanvas');

		// Перезаписывание корневого элемента у канваса, для изменения поведения фокусной ловушки
		window.bootstrap.Offcanvas.getOrCreateInstance(this.headerOffcanvas)._focustrap._config.trapElement = this.header;

		this.url = !PROD ?
			`${API.DEVELOP_CATALOG}/search/query` :
			SAND ?
				`${API.SAND_CATALOG}/search/query`:
				`${API.PROD_CATALOG}/search/query`;

		this.updateOffsets();

		forEach(this.triggers, (trigger) => { trigger.addEventListener('click', this.open); });
		window.addEventListener('resize', this.resize);
		this.input.addEventListener('input', this.search);
		this.block.addEventListener('click', this.clickHandler);

    this.block.classList.add('search_inited');
  };

	open = (event) => {
		event.preventDefault();
		this.block.classList.add('open');
		document.body.classList.add('search-open');
		document.addEventListener('keydown', this.keydownHandler);

		this.input.focus();

		this.history = [...new Set(JSON.parse(localStorage.getItem('search-history')) || [])];
		!this.input.value.length && this.renderHistory();
	};

	hide = () => {
		this.input.blur();
		this.block.classList.remove('open');
		document.body.classList.remove('search-open');
		document.removeEventListener('keydown', this.keydownHandler);
	};

	keydownHandler = ({ key }) => {
		if (key === 'Escape') this.hide();
		if (key === 'Enter') this.redirect();
	};

	clickHandler = (event) => {
		// При клике на подсказки или историю поиска текст подставляется в инпут
		const searchBy = event.target.dataset['searchBy'];
		if (searchBy) {
			event.preventDefault();
			this.input.value = searchBy;
			this.input.dispatchEvent(new Event('input'));
			return;
		}

		// При клике на крестик удаляется элемент истории
		const historyItem = event.target.dataset['deleteHistoryItem'];
		if (historyItem) {
			event.preventDefault();
			this.updateHistory(filter(this.history, (elem) => elem !== historyItem));
			this.input.focus();
			return;
		}

		// При клике clear all очищает историю
		if (event.target.closest('[data-clear-history]')) {
			event.preventDefault();
			this.updateHistory([]);
			return;
		}

		// При клике на поиск редирект на страницу поиска
		if (event.target.closest('[data-submit]')) {
			event.preventDefault();
			if (this.input.value) this.redirect();
			return;
		}

		// При клике крестик в инпуте очистка или закрытие поиска
		if (event.target.closest('[data-close-search]')) {
			event.preventDefault();
			if (this.input.value) {
				this.input.value = '';
				this.input.focus();
				this.input.dispatchEvent(new Event('input'));
			} else {
				this.hide();
			}
		}
	};

	resize = debounce(() => {
		this.updateOffsets();
	}, 250);

	search = debounce(async () => {
		const query = trim(this.input.value);
		this.oldQuery = query;
		this.toggleNotFound();
		if (query.length) {
			this.block.classList.add('search_loader');

			try {
				if (this.abortContoller) this.abortContoller.abort(); // отмена предыдущего запроса
				this.abortContoller = new AbortController();

				const response = await fetch(this.url, {
					headers: {
						'Content-Type': 'application/json'
					},
					signal: this.abortContoller.signal,
					method: 'POST',
					body: JSON.stringify({ query }),
				});

				if (response.ok) {
					const data = await response.json();
					const products = values(get(data, 'result.products', {}));
					const categories = get(data, 'result.categories', []);
					const suggestions = get(data, 'result.suggestions', []);
					this.redirectProductLink =  get(data, 'result.redirect_product_url', '');
					const link = get(data, 'result.link', '');

					this.content.innerHTML = '';
					if (products.length || categories.length) {
						this.renderCategories(categories);
						this.renderProducts(products, this.redirectProductLink || link);
						this.updateHistory([query, ...this.history]);
					} else {
						this.toggleNotFound(true);
					}

					this.renderSuggestions(suggestions);
					this.block.classList.remove('search_loader');
				} else {
					await Promise.reject(new Error(response.statusText));
				}

			} catch (e) {
				if (e.name === 'AbortError') return;

				this.content.innerHTML = '';
				this.renderTitle(window.BX?.message('searchError') || 'Что-то пошло не так, попробуйте ещё раз');
				this.block.classList.remove('search_loader');
			}
		} else {
			this.content.innerHTML = '';
		}

		this.content.classList.remove('custom-scrollbar_inited');
		window.Block.CustomScrollbar.init(this.block, true);
	}, 1000);

	redirect = () => {
		const query = trim(this.input.value);
		if (this.oldQuery !== query) this.redirectProductLink = '';

		const relativeURL = this.redirectProductLink || (window.BX?.message('urlToSearch') || '/search?q=#QUERY#').replace('#QUERY#', escape(query));
		const absoluteURL = new URL(relativeURL, window.location.href);
		window.location.href = absoluteURL.href;
	};

	renderHistory = () => {
		this.historyContainer.innerHTML = '';
		if (isEmpty(this.history)) return;
    this.historyContainer.classList.remove('custom-scrollbar_inited');

		this.historyContainer.insertAdjacentHTML('afterbegin', this.bh.apply([
			{block: 'search', elem: 'input-dropdown-header', content: [
					{elem: 'input-dropdown-title', content: window.BX?.message('searchHistory') || 'Вы недавно смотрели',},
					{elem: 'clear-history', tag: 'button', attrs: {type: 'button', 'data-clear-history': ''}, content: [
							window.BX?.message('clearAll') || 'Очистить историю',
							{block: 'fi', mods: {icon: 'trash'}},
						]},
				]},
      {block: 'search', elem: 'input-list', content: map(this.history, (text) => [
          {elem: 'input-list-item', content: [
              {elem: 'input-list-button', tag: 'button', attrs: {type: 'button', 'data-search-by': text}, content: escape(text)},
              {elem: 'delete-history-button', tag: 'button', attrs: {type: 'button', 'data-delete-history-item': text}},
            ]},
        ])},
		]));

    window.Block.CustomScrollbar.init(this.block, true);
	};

	renderSuggestions = (suggestions) => {
		this.block.querySelector('.search__suggestions')?.remove();
		if (isEmpty(suggestions)) return;

		this.content.insertAdjacentHTML('afterbegin', this.bh.apply([
			{block: 'search', elem: 'suggestions', content: map(suggestions, (text) => [
					{elem: 'suggestion-button', tag: 'button', attrs: {type: 'button', 'data-search-by': text}, content: text},
				])},
		]));
	};

	renderProducts = (products, link) => {
		if (isEmpty(products)) return;
		this.content.insertAdjacentHTML('beforeend', this.bh.apply([
			{cls: 'd-sm-flex justify-content-between align-items-end gap-8 mb-16', content: [
					{cls: 'h2 text-uppercase mb-0', content: `${window.BX?.message('searchProducts') || 'Товары'} <span class="text-secondary">${products.length}</span>`},
					{block: 'a', cls: 'me-16', mods: {underline: true}, attrs: {href: link}, content: window.BX?.message('searchAllResults') || 'Смотреть все'},
				]},
			{cls: 'row g-12', content: map(products, ({ photos, id }) => [
					{cls: 'col-12 col-sm-6 col-lg-4 col-xl-3', content: [
							productCardBase({
								attrs: {'data-id': id, 'data-search-card': true},
								images: photos,
							}),
						]}
				])},
		]));
		forEach(products, (data) => {
			new window.Block.ProductCard({
				selector: `.search [data-id="${data.id}"]:not(.product-card_inited)`, options: { data }
			});
		});
	};

	renderCategories = (categories) => {
		if (isEmpty(categories)) return;
		this.content.insertAdjacentHTML('beforeend', this.bh.apply([
			{cls: 'h2 text-uppercase mb-16', content: `${window.BX?.message('searchCategory') || 'Категории'} <span class="text-secondary">${categories.length}</span>`},
			{cls: 'row g-12 mb-60', content: map(categories, ({ text, href }) => [
					{cls: 'col-12', content: [
							{block: 'a', mods: {underline: true}, cls: 'd-inline-block fs-24', attrs: { href }, content: text}
						]},
				])},
		]));
	};

	renderTitle = (text) => {
		if (!text) return;
		this.content.insertAdjacentHTML('beforeend', this.bh.apply([
			{cls: 'h2 text-uppercase mb-0', content: text},
		]));
	};

	toggleNotFound = (toggle = false) => {
		this.block.classList.toggle('search_not-found', toggle);
	};

	updateHistory = (history) => {
		if (history.length > 10) {
			history.pop();
			this.updateHistory(history);
			return;
		}
		this.history = [...new Set(history)];
		localStorage.setItem('search-history', JSON.stringify(this.history));
		this.renderHistory();
	};

	updateOffsets = () => {
		// TODO: (после релиза) при открытии поиска на мобилке и ресайзе экрана, поиск уезжает
		this.headerBodyHeight = this.headerBody.offsetHeight;
		this.block.style.setProperty('--header-body-height', this.headerBodyHeight + 'px');
	};

	destroy = () => {
		window.removeEventListener('resize', this.resize);
		forEach(this.triggers, (trigger) => { trigger.removeEventListener('click', this.open); });
    if (this.block) {
			this.block.removeEventListener('click', this.clickHandler);
			this.input.removeEventListener('input', this.search);
      this.block.classList.remove('search_inited');
      delete this.block.Search;
    }
  };

  static init() {
    Array.from(document.querySelectorAll('.search:not(.search_inited)')).forEach((elem) => new Search(elem));
  }
}

document.addEventListener('DOMContentLoaded', Search.init);

// Экспорт;
window.Block.Search = Search;
